import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import * as api from "../../api";
import { useCompany } from "../../Context/CompanyContext";
import { beginLogout } from "../../utils/logoutGuard";
import { useSnackbar } from "../../utils/useSnackbar";
import IdleLockScreen from "./IdleLockScreen";
import {
  IDLE_TEST_SECONDS,
  LOCKED_KEY,
  LOGOUT_NOTICE_KEY,
  clearAllClientData,
  clearIdleState,
  readLastActivity,
  readLocked,
  readUnlockAttempts,
  writeLastActivity,
  writeLocked,
  writeUnlockAttempts,
} from "./idleSessionStore";

// Idle Session Security - SuperAdmin entitles it (Global Settings > Special
// Features), each store's admin configures it (Settings > Idle Session
// Security, see Settings/IdleSessionSettings.js):
//  - Screen lock: after idleLockMinutes without input the app is blurred
//    behind a lock screen. Only the signed-in user's password opens it again,
//    and the session carries on exactly where it was (same page, same cart).
//  - Auto logout: after idleLogoutMinutes without input the session is
//    ended and this browser's stored data is wiped. A countdown is shown for
//    the last idleLogoutWarningSeconds, always on the lock screen (it locks
//    then even if screen lock is off), so staying signed in needs the password.
// Both clocks run from the same "last activity" time. While locked, mouse
// movement doesn't count as activity - only unlocking with the password does.

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "wheel", "scroll"];
// Activity is noticed instantly in memory; sharing it with other tabs via
// localStorage is throttled so mousemove doesn't write on every pixel.
const SHARE_THROTTLE_MS = 1000;
const TICK_MS = 1000;
export const MAX_UNLOCK_ATTEMPTS = 5;
// Auto logout still has to finish when the server is unreachable.
const LOGOUT_API_TIMEOUT_MS = 3000;
const OVERLAY_SELECTOR = "[data-idle-overlay]";

const clampMinutes = (value, fallback, min, max) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.min(Math.max(n, min), max) : fallback;
};

export default function IdleSessionGuard({ user, testMode = false }) {
  const { company, rawCompany } = useCompany();
  const dispatch = useDispatch();
  const history = useHistory();
  const [openSnackbar] = useSnackbar();

  // Nothing is enforced until the real settings have loaded - DEFAULT_COMPANY
  // has both features off. A lock carried over from before a reload still
  // shows during that gap (fail closed), see `locked` below.
  const loaded = Boolean(rawCompany);
  const entitled = company.idleSessionEntitled === true;
  // SuperAdmin test mode: lock only, after IDLE_TEST_SECONDS, regardless of
  // the store settings (App.js only passes it for a SuperAdmin in the POS).
  const lockEnabled = testMode || (loaded && entitled && company.idleLockEnabled === true);
  const logoutEnabled = !testMode && loaded && entitled && company.idleLogoutEnabled === true;
  const active = lockEnabled || logoutEnabled;
  const lockMinutes = clampMinutes(company.idleLockMinutes, 2, 1, 240);
  const logoutMs = clampMinutes(company.idleLogoutMinutes, 10, 2, 720) * 60000;
  const warnMs = Math.min(
    Math.max(Number(company.idleLogoutWarningSeconds) || 120, 15) * 1000,
    logoutMs - 1000
  );

  const [locked, setLocked] = useState(readLocked);
  // Whole seconds until auto logout while the countdown is showing, else null.
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [unlockAttempts, setUnlockAttempts] = useState(readUnlockAttempts);

  const lockedRef = useRef(locked);
  const warningRef = useRef(false);
  const lastActivityRef = useRef(readLastActivity() || Date.now());
  const lastShareRef = useRef(0);
  const endingRef = useRef(false);
  // The interval/listeners read timings through this ref so a settings
  // change doesn't have to tear them down and restart the idle clock.
  const configRef = useRef({});
  configRef.current = {
    lockEnabled,
    logoutEnabled,
    lockMs: testMode ? IDLE_TEST_SECONDS * 1000 : lockMinutes * 60000,
    logoutMs,
    warnMs,
  };

  const applyLocked = useCallback((value) => {
    lockedRef.current = value;
    setLocked(value);
  }, []);

  const clearWarning = useCallback(() => {
    warningRef.current = false;
    setSecondsLeft(null);
  }, []);

  const markActive = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    lastShareRef.current = now;
    writeLastActivity(now);
  }, []);

  // `notice` is shown on the login screen (see Login.js); a manual sign-out
  // from the lock screen or the countdown gets the usual toast instead.
  const endSession = useCallback(
    async ({ reason, notice } = {}) => {
      if (endingRef.current) return;
      endingRef.current = true;
      beginLogout();
      try {
        await Promise.race([
          api.logoutSession(reason),
          new Promise((resolve) => setTimeout(resolve, LOGOUT_API_TIMEOUT_MS)),
        ]);
      } catch (err) {
        // Server unreachable - the local sign-out below still happens.
      }
      await clearAllClientData();
      if (notice) {
        try {
          sessionStorage.setItem(LOGOUT_NOTICE_KEY, notice);
        } catch (err) {
          // Storage blocked - the login screen just won't say why.
        }
      }
      // Flips App.js to its logged-out branch, which unmounts this guard -
      // so nothing below may touch this component's state.
      dispatch({ type: "LOGOUT" });
      if (!notice) openSnackbar("Logged out successfully");
      history.push("/");
    },
    [dispatch, history, openSnackbar]
  );

  const tick = useCallback(() => {
    if (endingRef.current) return;
    const config = configRef.current;
    if (!config.lockEnabled && !config.logoutEnabled) return;

    // Another tab may have seen more recent activity.
    const shared = readLastActivity();
    if (shared && shared > lastActivityRef.current) lastActivityRef.current = shared;

    const idle = Date.now() - lastActivityRef.current;
    if (config.logoutEnabled && idle >= config.logoutMs) {
      endSession({ reason: "idle", notice: "idle" });
      return;
    }
    if (config.lockEnabled && !lockedRef.current && idle >= config.lockMs) {
      writeLocked(true);
      applyLocked(true);
    }
    const inWarning = config.logoutEnabled && idle >= config.logoutMs - config.warnMs;
    // The countdown always runs on the lock screen, so staying signed in
    // takes the user's password - even when screen lock itself is off.
    if (inWarning && !lockedRef.current) {
      writeLocked(true);
      applyLocked(true);
    }
    warningRef.current = inWarning;
    // Same-value updates (null -> null) don't re-render, so this is free
    // outside the countdown window.
    setSecondsLeft(inWarning ? Math.max(0, Math.ceil((config.logoutMs - idle) / 1000)) : null);
  }, [applyLocked, endSession]);

  // Feature switched off (or a lock left over from a reload once settings
  // load and say it's off): never leave a till stuck behind a lock screen
  // nobody can configure any more.
  useEffect(() => {
    if (!loaded) return;
    if (!active) {
      clearIdleState();
      clearWarning();
    }
    if (!active && lockedRef.current) {
      writeLocked(false);
      applyLocked(false);
    }
  }, [loaded, active, applyLocked, clearWarning]);

  useEffect(() => {
    if (!active) return undefined;

    // First time on for this session - start the clock now rather than
    // from whenever the page happened to load.
    if (!readLastActivity()) markActive();

    const onActivity = () => {
      if (lockedRef.current || warningRef.current || endingRef.current) return;
      const now = Date.now();
      lastActivityRef.current = now;
      if (now - lastShareRef.current >= SHARE_THROTTLE_MS) {
        lastShareRef.current = now;
        writeLastActivity(now);
      }
    };

    // A background tab's timers are throttled, so catch up on return.
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };

    const onStorage = (event) => {
      if (endingRef.current) return;
      // localStorage.clear() in another tab arrives with key === null.
      if ((event.key === "profile" || event.key === null) && !localStorage.getItem("profile")) {
        // Signed out in another tab (auto logout, Logout button, expired
        // token) - follow it rather than keep a dead session on screen.
        endingRef.current = true;
        beginLogout();
        dispatch({ type: "LOGOUT" });
        history.push("/");
        return;
      }
      if (event.key === LOCKED_KEY || event.key === null) {
        const nowLocked = readLocked();
        if (nowLocked !== lockedRef.current) applyLocked(nowLocked);
        if (!nowLocked) {
          // Unlocked in another tab - it also reset the shared clock.
          lastActivityRef.current = readLastActivity() || Date.now();
          setUnlockAttempts(readUnlockAttempts());
          clearWarning();
        }
      }
    };

    ACTIVITY_EVENTS.forEach((type) =>
      window.addEventListener(type, onActivity, { capture: true, passive: true })
    );
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("storage", onStorage);
    const interval = setInterval(tick, TICK_MS);
    tick();

    return () => {
      ACTIVITY_EVENTS.forEach((type) =>
        window.removeEventListener(type, onActivity, { capture: true, passive: true })
      );
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("storage", onStorage);
      clearInterval(interval);
    };
  }, [active, tick, markActive, applyLocked, clearWarning, dispatch, history]);

  const overlayVisible = locked || secondsLeft !== null;

  // While an overlay is up, the app underneath must not be reachable - not
  // by Tab, not by a barcode scanner typing into the POS search box. `inert`
  // takes everything else on the page out of focus/click order: the React
  // root, and also MUI dialogs, which portal straight into <body> - an open
  // dialog's focus trap would otherwise keep pulling focus off the password
  // field. Dialogs opened while locked get the same treatment via the
  // observer. The capture-phase key blocker covers keyboard listeners
  // attached to window/document.
  useEffect(() => {
    if (!overlayVisible) return undefined;
    // element -> whether we also added aria-hidden (MUI sets it on the
    // siblings of an open dialog, and that must survive our cleanup).
    const madeInert = new Map();
    const makeInert = (el) => {
      if (el.matches?.(`${OVERLAY_SELECTOR}, script, style, link`) || el.hasAttribute("inert")) return;
      const addHidden = !el.hasAttribute("aria-hidden");
      el.setAttribute("inert", "");
      if (addHidden) el.setAttribute("aria-hidden", "true");
      madeInert.set(el, addHidden);
    };
    Array.from(document.body.children).forEach(makeInert);
    const observer = new MutationObserver((mutations) =>
      mutations.forEach((m) => m.addedNodes.forEach((node) => node.nodeType === 1 && makeInert(node)))
    );
    observer.observe(document.body, { childList: true });

    const blockKeys = (event) => {
      if (event.target?.closest?.(OVERLAY_SELECTOR)) return;
      event.stopImmediatePropagation();
      event.preventDefault();
    };
    ["keydown", "keypress", "keyup"].forEach((type) => window.addEventListener(type, blockKeys, true));
    return () => {
      observer.disconnect();
      madeInert.forEach((addedHidden, el) => {
        el.removeAttribute("inert");
        if (addedHidden) el.removeAttribute("aria-hidden");
      });
      ["keydown", "keypress", "keyup"].forEach((type) => window.removeEventListener(type, blockKeys, true));
    };
  }, [overlayVisible]);

  const handleUnlock = useCallback(
    async (password) => {
      try {
        await api.unlockSession(password);
        writeUnlockAttempts(0);
        setUnlockAttempts(0);
        markActive();
        writeLocked(false);
        applyLocked(false);
        clearWarning();
        return { ok: true };
      } catch (err) {
        const status = err?.response?.status;
        const message = err?.response?.data?.message;
        if (status === 403 || status === 404) {
          // Account deactivated/removed while locked - nothing to unlock into.
          endSession({ notice: "inactive" });
          return { ok: false, message: message || "This account can no longer sign in." };
        }
        if (status === 400 && password) {
          const attempts = readUnlockAttempts() + 1;
          writeUnlockAttempts(attempts);
          setUnlockAttempts(attempts);
          if (attempts >= MAX_UNLOCK_ATTEMPTS) {
            endSession({ reason: "unlock_attempts", notice: "unlock_attempts" });
            return { ok: false, message: "Too many incorrect attempts. Signing out…" };
          }
          const left = MAX_UNLOCK_ATTEMPTS - attempts;
          return {
            ok: false,
            message: `${message || "Incorrect password"}. ${left} attempt${left === 1 ? "" : "s"} left before sign-out.`,
          };
        }
        return {
          ok: false,
          message: message || "Couldn't reach the server. Check the connection and try again.",
        };
      }
    },
    [applyLocked, clearWarning, endSession, markActive]
  );

  const handleSignOut = useCallback(() => endSession(), [endSession]);

  if (locked) {
    return (
      <IdleLockScreen
        user={user?.result}
        businessName={rawCompany?.businessName}
        // With screen lock off, the lock only comes up with the countdown.
        lockMinutes={testMode ? IDLE_TEST_SECONDS / 60 : lockEnabled ? lockMinutes : Math.round((logoutMs - warnMs) / 6000) / 10}
        testMode={testMode}
        secondsLeft={secondsLeft}
        warnSeconds={Math.round(warnMs / 1000)}
        attemptsLeft={MAX_UNLOCK_ATTEMPTS - unlockAttempts}
        onUnlock={handleUnlock}
        onSignOut={handleSignOut}
      />
    );
  }

  return null;
}
